import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Cloud, TrendingDown, TrendingUp, Activity, AlertTriangle, RefreshCw, X, ChevronDown, ChevronUp } from 'lucide-react';
import { ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, Brush, ReferenceLine } from 'recharts';

interface IchimokuData {
  date: string;
  close: number;
  high: number;
  low: number;
  tenkanSen: number;
  kijunSen: number;
  senkouSpanA: number;
  senkouSpanB: number;
  chikouSpan: number;
}

interface IchimokuAlert {
  type: 'KUMO_BREAK_DOWN' | 'WITHIN_KUMO' | 'KUMO_EXIT_DOWN' | 'TENKAN_TOUCH_UP' | 'TENKAN_TOUCH_DOWN' | 'KIJUN_TOUCH_UP' | 'KIJUN_TOUCH_DOWN';
  description: string;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
}

interface IchimokuResult {
  ticker: string;
  timeframe: 'daily' | 'weekly';
  currentPrice: number;
  tenkanSen: number;
  kijunSen: number;
  senkouSpanA: number;
  senkouSpanB: number;
  chikouSpan: number;
  alerts: IchimokuAlert[];
  priceHistory: IchimokuData[];
  error?: string;
}

interface IchimokuSummary {
  total: number;
  kumoBreakDown: number;
  withinKumo: number;
  kumoExitDown: number;
  tenkanTouch: number;
  kijunTouch: number;
  totalWithAlerts: number;
  errors: number;
}

interface Props {
  tickers: string[];
}

const alertTypeLabels: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  'KUMO_BREAK_DOWN': { label: 'Kumo Break ↓', color: 'bg-red-500', icon: <TrendingDown className="w-3 h-3" /> },
  'WITHIN_KUMO': { label: 'In Kumo', color: 'bg-yellow-500', icon: <Cloud className="w-3 h-3" /> },
  'KUMO_EXIT_DOWN': { label: 'Kumo Exit ↓', color: 'bg-red-600', icon: <TrendingDown className="w-3 h-3" /> },
  'TENKAN_TOUCH_UP': { label: 'Tenkan ↑', color: 'bg-green-500', icon: <TrendingUp className="w-3 h-3" /> },
  'TENKAN_TOUCH_DOWN': { label: 'Tenkan ↓', color: 'bg-orange-500', icon: <TrendingDown className="w-3 h-3" /> },
  'KIJUN_TOUCH_UP': { label: 'Kijun ↑', color: 'bg-blue-500', icon: <TrendingUp className="w-3 h-3" /> },
  'KIJUN_TOUCH_DOWN': { label: 'Kijun ↓', color: 'bg-purple-500', icon: <TrendingDown className="w-3 h-3" /> },
};

export const IchimokuAlerts = ({ tickers }: Props) => {
  const { toast } = useToast();
  const [activeTimeframe, setActiveTimeframe] = useState<'daily' | 'weekly'>('daily');
  const [dailyResults, setDailyResults] = useState<IchimokuResult[]>([]);
  const [weeklyResults, setWeeklyResults] = useState<IchimokuResult[]>([]);
  const [dailySummary, setDailySummary] = useState<IchimokuSummary | null>(null);
  const [weeklySummary, setWeeklySummary] = useState<IchimokuSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedStock, setSelectedStock] = useState<IchimokuResult | null>(null);
  const [showAlertsOnly, setShowAlertsOnly] = useState(false);
  const [timeRange, setTimeRange] = useState<'1M' | '3M' | '6M' | '1Y' | 'ALL'>('3M');

  const results = activeTimeframe === 'daily' ? dailyResults : weeklyResults;
  const summary = activeTimeframe === 'daily' ? dailySummary : weeklySummary;

  const filteredResults = useMemo(() => {
    if (!showAlertsOnly) return results;
    return results.filter(r => r.alerts.length > 0);
  }, [results, showAlertsOnly]);

  const runAnalysis = async () => {
    if (tickers.length === 0) {
      toast({
        title: "No Positions",
        description: "Please import positions first",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setProgress(10);
    setDailyResults([]);
    setWeeklyResults([]);
    setDailySummary(null);
    setWeeklySummary(null);
    setSelectedStock(null);

    try {
      // Run daily analysis
      setProgress(20);
      const { data: dailyData, error: dailyError } = await supabase.functions.invoke('ichimoku-analysis', {
        body: { tickers, timeframe: 'daily' }
      });

      if (dailyError) throw dailyError;
      setDailyResults(dailyData.results || []);
      setDailySummary(dailyData.summary || null);

      setProgress(60);

      // Run weekly analysis
      const { data: weeklyData, error: weeklyError } = await supabase.functions.invoke('ichimoku-analysis', {
        body: { tickers, timeframe: 'weekly' }
      });

      if (weeklyError) throw weeklyError;
      setWeeklyResults(weeklyData.results || []);
      setWeeklySummary(weeklyData.summary || null);

      setProgress(100);
      
      toast({
        title: "Analysis Complete",
        description: `Analyzed ${dailyData.results?.length || 0} stocks for Ichimoku signals`,
      });
    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 500);
    }
  };

  const getAlertBadges = (alerts: IchimokuAlert[]) => {
    if (alerts.length === 0) {
      return <Badge variant="outline" className="text-muted-foreground">No alerts</Badge>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {alerts.map((alert, idx) => {
          const config = alertTypeLabels[alert.type];
          return (
            <Badge key={idx} className={`${config.color} text-white text-xs flex items-center gap-1`}>
              {config.icon}
              {config.label}
            </Badge>
          );
        })}
      </div>
    );
  };

  const getChartData = useMemo(() => {
    if (!selectedStock?.priceHistory) return [];
    
    const data = selectedStock.priceHistory;
    let sliceCount = data.length;
    
    switch (timeRange) {
      case '1M': sliceCount = activeTimeframe === 'weekly' ? 4 : 22; break;
      case '3M': sliceCount = activeTimeframe === 'weekly' ? 13 : 66; break;
      case '6M': sliceCount = activeTimeframe === 'weekly' ? 26 : 132; break;
      case '1Y': sliceCount = activeTimeframe === 'weekly' ? 52 : 252; break;
      case 'ALL': sliceCount = data.length; break;
    }
    
    return data.slice(-Math.min(sliceCount, data.length));
  }, [selectedStock, timeRange, activeTimeframe]);

  const StockChart = () => {
    if (!selectedStock) return null;

    const timeRangeButtons: ('1M' | '3M' | '6M' | '1Y' | 'ALL')[] = ['1M', '3M', '6M', '1Y', 'ALL'];

    // Get current values for reference lines where alerts occurred
    const alertPoints = selectedStock.alerts.map(alert => ({
      type: alert.type,
      description: alert.description,
    }));

    return (
      <div className="space-y-4">
        {/* Time Range Selector */}
        <div className="flex justify-center gap-2">
          {timeRangeButtons.map((range) => (
            <Button
              key={range}
              variant={timeRange === range ? "default" : "outline"}
              size="sm"
              onClick={() => setTimeRange(range)}
            >
              {range}
            </Button>
          ))}
        </div>

        {/* Alert Badges */}
        <div className="flex flex-wrap gap-2 justify-center">
          {selectedStock.alerts.map((alert, idx) => {
            const config = alertTypeLabels[alert.type];
            return (
              <Badge key={idx} className={`${config.color} text-white flex items-center gap-1`}>
                {config.icon}
                {alert.description}
              </Badge>
            );
          })}
          {selectedStock.alerts.length === 0 && (
            <Badge variant="outline">No active alerts</Badge>
          )}
        </div>

        {/* Chart */}
        <div className="h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={getChartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />
              <YAxis 
                domain={['auto', 'auto']}
                tick={{ fontSize: 10 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={(value: number, name: string) => [
                  `$${value.toFixed(2)}`,
                  name.replace(/([A-Z])/g, ' $1').trim()
                ]}
              />
              <Legend />
              
              {/* Kumo Cloud (area between Senkou Span A and B) */}
              <Area
                type="monotone"
                dataKey="senkouSpanA"
                stroke="none"
                fill="rgba(76, 175, 80, 0.15)"
                name="Senkou A"
              />
              <Area
                type="monotone"
                dataKey="senkouSpanB"
                stroke="none"
                fill="rgba(244, 67, 54, 0.15)"
                name="Senkou B"
              />
              
              {/* Senkou Span Lines */}
              <Line
                type="monotone"
                dataKey="senkouSpanA"
                stroke="#4CAF50"
                strokeWidth={1}
                dot={false}
                name="Senkou Span A"
              />
              <Line
                type="monotone"
                dataKey="senkouSpanB"
                stroke="#F44336"
                strokeWidth={1}
                dot={false}
                name="Senkou Span B"
              />
              
              {/* Tenkan-Sen (Conversion Line) */}
              <Line
                type="monotone"
                dataKey="tenkanSen"
                stroke="#2196F3"
                strokeWidth={1.5}
                dot={false}
                name="Tenkan-Sen"
              />
              
              {/* Kijun-Sen (Base Line) */}
              <Line
                type="monotone"
                dataKey="kijunSen"
                stroke="#9C27B0"
                strokeWidth={1.5}
                dot={false}
                name="Kijun-Sen"
              />
              
              {/* Price Line */}
              <Line
                type="monotone"
                dataKey="close"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
                name="Price"
              />

              {/* Brush for zoom/scroll */}
              <Brush
                dataKey="date"
                height={30}
                stroke="hsl(var(--primary))"
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Ichimoku Values */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
          <div className="p-2 bg-muted rounded text-center">
            <div className="text-muted-foreground text-xs">Price</div>
            <div className="font-semibold">${selectedStock.currentPrice}</div>
          </div>
          <div className="p-2 bg-blue-500/10 rounded text-center">
            <div className="text-blue-600 text-xs">Tenkan</div>
            <div className="font-semibold">${selectedStock.tenkanSen}</div>
          </div>
          <div className="p-2 bg-purple-500/10 rounded text-center">
            <div className="text-purple-600 text-xs">Kijun</div>
            <div className="font-semibold">${selectedStock.kijunSen}</div>
          </div>
          <div className="p-2 bg-green-500/10 rounded text-center">
            <div className="text-green-600 text-xs">Span A</div>
            <div className="font-semibold">${selectedStock.senkouSpanA}</div>
          </div>
          <div className="p-2 bg-red-500/10 rounded text-center">
            <div className="text-red-600 text-xs">Span B</div>
            <div className="font-semibold">${selectedStock.senkouSpanB}</div>
          </div>
          <div className="p-2 bg-muted rounded text-center">
            <div className="text-muted-foreground text-xs">Chikou</div>
            <div className="font-semibold">${selectedStock.chikouSpan}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-primary" />
              Ichimoku Cloud Analysis
            </CardTitle>
            <CardDescription>
              Detect Kumo cloud breakouts, Tenkan/Kijun touches across daily and weekly timeframes
            </CardDescription>
          </div>
          <Button
            onClick={runAnalysis}
            disabled={loading || tickers.length === 0}
          >
            {loading ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Activity className="mr-2 h-4 w-4" />
            )}
            Run Analysis
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && (
          <div className="space-y-2">
            <Progress value={progress} />
            <p className="text-sm text-muted-foreground text-center">
              Analyzing {tickers.length} stocks with Ichimoku indicators...
            </p>
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-red-600">{summary.kumoBreakDown}</div>
                <div className="text-xs text-red-600">Kumo Break ↓</div>
              </CardContent>
            </Card>
            <Card className="bg-yellow-500/10 border-yellow-500/30">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{summary.withinKumo}</div>
                <div className="text-xs text-yellow-600">Within Kumo</div>
              </CardContent>
            </Card>
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{summary.tenkanTouch}</div>
                <div className="text-xs text-blue-600">Tenkan Touch</div>
              </CardContent>
            </Card>
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{summary.kijunTouch}</div>
                <div className="text-xs text-purple-600">Kijun Touch</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Timeframe Tabs */}
        {results.length > 0 && (
          <Tabs value={activeTimeframe} onValueChange={(v) => setActiveTimeframe(v as 'daily' | 'weekly')}>
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="daily">Daily</TabsTrigger>
                <TabsTrigger value="weekly">Weekly</TabsTrigger>
              </TabsList>
              <Button
                variant={showAlertsOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowAlertsOnly(!showAlertsOnly)}
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                {showAlertsOnly ? 'Showing Alerts Only' : 'Show Alerts Only'}
                {showAlertsOnly && (
                  <Badge variant="secondary" className="ml-2">
                    {filteredResults.length}
                  </Badge>
                )}
              </Button>
            </div>

            <TabsContent value="daily" className="mt-4">
              <ResultsTable results={filteredResults} onRowClick={setSelectedStock} getAlertBadges={getAlertBadges} />
            </TabsContent>
            <TabsContent value="weekly" className="mt-4">
              <ResultsTable results={filteredResults} onRowClick={setSelectedStock} getAlertBadges={getAlertBadges} />
            </TabsContent>
          </Tabs>
        )}

        {/* Stock Chart Dialog */}
        <Dialog open={!!selectedStock} onOpenChange={() => setSelectedStock(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Cloud className="h-5 w-5" />
                {selectedStock?.ticker} - Ichimoku Cloud ({activeTimeframe.charAt(0).toUpperCase() + activeTimeframe.slice(1)})
              </DialogTitle>
              <DialogDescription>
                Price: ${selectedStock?.currentPrice} | {selectedStock?.alerts.length || 0} active alert(s)
              </DialogDescription>
            </DialogHeader>
            <StockChart />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Results Table Component
interface ResultsTableProps {
  results: IchimokuResult[];
  onRowClick: (result: IchimokuResult) => void;
  getAlertBadges: (alerts: IchimokuAlert[]) => React.ReactNode;
}

const ResultsTable = ({ results, onRowClick, getAlertBadges }: ResultsTableProps) => {
  if (results.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No results to display. Run the analysis first.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Ticker</TableHead>
          <TableHead className="text-right">Price</TableHead>
          <TableHead className="text-right">Tenkan</TableHead>
          <TableHead className="text-right">Kijun</TableHead>
          <TableHead className="text-right">Span A</TableHead>
          <TableHead className="text-right">Span B</TableHead>
          <TableHead>Alerts</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.map((result, index) => (
          <TableRow
            key={index}
            className="cursor-pointer hover:bg-muted/50"
            onClick={() => onRowClick(result)}
          >
            <TableCell className="font-semibold">{result.ticker}</TableCell>
            <TableCell className="text-right">${result.currentPrice}</TableCell>
            <TableCell className="text-right text-blue-600">${result.tenkanSen}</TableCell>
            <TableCell className="text-right text-purple-600">${result.kijunSen}</TableCell>
            <TableCell className="text-right text-green-600">${result.senkouSpanA}</TableCell>
            <TableCell className="text-right text-red-600">${result.senkouSpanB}</TableCell>
            <TableCell>{getAlertBadges(result.alerts)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default IchimokuAlerts;
